import React, { useMemo } from "react";
import { Redirect, Route, Switch, useLocation } from "react-router-dom";

import Auth0UserProps from "src/custom-prop-types/auth0user";

import { useUserPerson } from "src/contexts/global/GlobalContext";
import {
  useWorkspaceFeatureFlags,
  useIsWorkspaceLoading,
} from "src/contexts/global/WorkspaceContext";
import { useOrgFeatureFlagsForTenant } from "src/hooks/orgConfig/hooks/useOrgFeatureFlagsForTenant";

import RestrictedRoute from "src/app/Restricted/Route";
import useDefaultRoutePath from "src/app/Restricted/useDefaultRoutePath";
import PATHS from "../consts/urlPaths";
import { stripFormatCharacters } from "../util/stripFormatCharacters";
import useSegment from "./useSegment";

import { PageTitle } from "./Helmet";
import getRoutes from "./getRoutes";

const Routes = (props) => {
  const { user } = props;

  const userPerson = useUserPerson();
  const userPersonAggregateId = userPerson?.aggregateId;
  const location = useLocation();
  const { pathname } = location;
  const cleanPathname = stripFormatCharacters(pathname);
  const {
    disablePersonalisation,
    enableAIChat,
    enableAIDataDrivenDashboards,
    enableDemoODOptions,
    enableOrgDesign,
    enableTeamCanvas,
    enablePositionBackedVacancies,
    enableSlidesTeamByTeamPagination,
    enableWorkforceGuardrails,
  } = useWorkspaceFeatureFlags();
  const workspaceFlagsLoading = useIsWorkspaceLoading();
  const { featureFlags, loading: orgConfigLoading } =
    useOrgFeatureFlagsForTenant();
  const enableDesigns = featureFlags?.designs?.enableDesigns ?? false;
  const defaultRoutePath = useDefaultRoutePath();

  const routes = useMemo(
    () =>
      getRoutes(
        props,
        userPersonAggregateId,
        disablePersonalisation,
        enableDesigns,
        orgConfigLoading,
        Boolean(enableDemoODOptions),
        workspaceFlagsLoading,
        enableAIChat,
        Boolean(enableTeamCanvas),
        Boolean(enableOrgDesign),
        Boolean(enablePositionBackedVacancies),
        Boolean(enableAIDataDrivenDashboards),
        Boolean(enableSlidesTeamByTeamPagination),
        Boolean(enableWorkforceGuardrails)
      ),
    // Route definitions depend on user identity, not the props object reference.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      user,
      userPersonAggregateId,
      disablePersonalisation,
      enableDesigns,
      orgConfigLoading,
      enableDemoODOptions,
      workspaceFlagsLoading,
      enableAIChat,
      enableTeamCanvas,
      enableOrgDesign,
      enablePositionBackedVacancies,
      enableAIDataDrivenDashboards,
      enableSlidesTeamByTeamPagination,
      enableWorkforceGuardrails,
    ]
  );

  // Set up analytics for Route / User change
  useSegment(user, userPerson, routes);

  if (cleanPathname !== pathname) {
    return (
      <Redirect
        push={false}
        to={{
          pathname: cleanPathname,
          search: location.search,
          hash: location.hash,
        }}
      />
    );
  }

  return (
    <Switch>
      {/* Adding a rule to detect an extra slash at the end of url and redirect to the same url without the slash */}
      <Redirect
        from="/:url*(/+)"
        to={{ ...location, pathname: pathname.slice(0, -1) }}
      />

      {routes.map((route) => {
        if (route.isDisabled) {
          return null;
        }

        const render = (rProps) => {
          return (
            <>
              <PageTitle title={route.title} id={rProps?.match.params.id} />
              <route.Component {...rProps} {...props} />
            </>
          );
        };

        return route.isRestricted ? (
          <RestrictedRoute key={route.path} {...route} render={render} />
        ) : (
          <Route key={route.path} {...route} render={render} />
        );
      })}

      {/* Summary page renamed to Insights */}
      <Redirect from={PATHS.SUMMARY_LEGACY} to={PATHS.INSIGHTS} />

      {/* Redirect added so that shared-components from TD can redirect to the correct Peron URLs */}
      <Redirect
        from={PATHS.PERSON_DETAILS_FROM_DIRECTORY}
        to={`${PATHS.PERSON_DETAILS_FROM_DIRECTORY}/details`}
      />

      {/* Redirect added so that shared-components from TD can redirect to the correct Team URLs */}
      <Redirect
        from={PATHS.TEAM_DETAILS_BASE_PATH}
        to={`${PATHS.TEAM_DETAILS_BASE_PATH}/details`}
      />

      <Redirect
        from={PATHS.WORKSPACE_SETTINGS_ROOT}
        to={`${PATHS.WORKSPACE_SETTINGS_ROOT}/details`}
      />

      <Redirect
        from={PATHS.WORKFORCE_ROOT}
        to={`${PATHS.WORKFORCE_ROOT}/positions`}
      />

      <Route>
        <Redirect to={{ pathname: defaultRoutePath }} />
      </Route>
    </Switch>
  );
};

Routes.propTypes = {
  user: Auth0UserProps,
};

export default Routes;
